import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const VIEWS = ["week", "month", "year"];

const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.toDateString() === b.toDateString();

const Calendar = ({ events, lang, onEventClick, initialView = "week" }) => {
  const [view, setView] = useState(initialView);
  const [cursor, setCursor] = useState(new Date());
  const loc = lang === "de" ? "de-DE" : "en-US";
  const t = (de, en) => (lang === "de" ? de : en);

  const move = (dir) => {
    const x = new Date(cursor);
    if (view === "week") x.setDate(x.getDate() + dir * 7);
    else if (view === "month") x.setMonth(x.getMonth() + dir);
    else x.setFullYear(x.getFullYear() + dir);
    setCursor(x);
  };

  const evByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { const k = new Date(e.start).toDateString(); (map[k] = map[k] || []).push(e); });
    return map;
  }, [events]);

  const label = view === "week"
    ? `${startOfWeek(cursor).toLocaleDateString(loc, { day: "2-digit", month: "short" })} – ${addDays(startOfWeek(cursor), 6).toLocaleDateString(loc, { day: "2-digit", month: "short", year: "numeric" })}`
    : view === "month" ? cursor.toLocaleDateString(loc, { month: "long", year: "numeric" })
    : cursor.getFullYear();

  return (
    <div data-testid="calendar" className="bg-[#0A0A0A] border border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="p-2 border border-white/15 text-white hover:border-[#ff0033]"><ChevronLeft size={16} /></button>
          <span className="font-heading text-lg md:text-xl uppercase tracking-tight text-white px-2">{label}</span>
          <button onClick={() => move(1)} className="p-2 border border-white/15 text-white hover:border-[#ff0033]"><ChevronRight size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="ml-2 font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#ff0033]">{t("Heute", "Today")}</button>
        </div>
        <div className="flex gap-1">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`px-3 py-2 font-mono-lab text-[10px] uppercase tracking-widest ${view === v ? "bg-[#ff0033] text-white" : "text-neutral-400 border border-white/15"}`}>
              {v === "week" ? t("Woche", "Week") : v === "month" ? t("Monat", "Month") : t("Jahr", "Year")}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 md:p-4">
        {view === "week" && <WeekView cursor={cursor} evByDay={evByDay} loc={loc} onEventClick={onEventClick} />}
        {view === "month" && <MonthView cursor={cursor} evByDay={evByDay} loc={loc} onEventClick={onEventClick} />}
        {view === "year" && <YearView cursor={cursor} events={events} loc={loc} setView={setView} setCursor={setCursor} />}
      </div>
    </div>
  );
};

const EventChip = ({ e, onEventClick, lang }) => (
  <button onClick={() => onEventClick(e)} data-testid={`cal-event-${e.id}`}
    className="w-full text-left px-2 py-1 mb-1 text-[10px] leading-tight rounded-sm border-l-2 hover:brightness-125 transition"
    style={{ borderColor: e.color, background: `${e.color}1f` }}>
    <span className="block text-white truncate font-medium">{new Date(e.start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} {e.title}</span>
    <span className={e.free_spots > 0 ? "text-neutral-400" : "text-[#ff0033]"}>{e.free_spots > 0 ? `${e.free_spots} frei` : "Ausgebucht"}</span>
  </button>
);

const WeekView = ({ cursor, evByDay, loc, onEventClick }) => {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
      {days.map((d) => {
        const evs = (evByDay[d.toDateString()] || []).sort((a, b) => a.start.localeCompare(b.start));
        const today = sameDay(d, new Date());
        return (
          <div key={d} className={`min-h-[120px] border border-white/10 p-2 ${today ? "bg-[#ff0033]/5 border-[#ff0033]/40" : ""}`}>
            <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{d.toLocaleDateString(loc, { weekday: "short", day: "2-digit" })}</p>
            {evs.map((e) => <EventChip key={e.id} e={e} onEventClick={onEventClick} />)}
          </div>
        );
      })}
    </div>
  );
};

const MonthView = ({ cursor, evByDay, loc, onEventClick }) => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-7 gap-1">
      {cells.map((d) => {
        const evs = (evByDay[d.toDateString()] || []);
        const inMonth = d.getMonth() === cursor.getMonth();
        const today = sameDay(d, new Date());
        return (
          <div key={d} className={`min-h-[90px] border border-white/10 p-1.5 ${inMonth ? "" : "opacity-30"} ${today ? "border-[#ff0033]/50" : ""}`}>
            <p className="text-[10px] text-neutral-500">{d.getDate()}</p>
            {evs.slice(0, 3).map((e) => <EventChip key={e.id} e={e} onEventClick={onEventClick} />)}
            {evs.length > 3 && <p className="text-[9px] text-neutral-500">+{evs.length - 3}</p>}
          </div>
        );
      })}
    </div>
  );
};

const YearView = ({ cursor, events, loc, setView, setCursor }) => {
  const year = cursor.getFullYear();
  const byMonth = {};
  events.forEach((e) => { const d = new Date(e.start); if (d.getFullYear() === year) (byMonth[d.getMonth()] = byMonth[d.getMonth()] || []).push(e); });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 12 }, (_, m) => {
        const evs = (byMonth[m] || []).sort((a, b) => a.start.localeCompare(b.start));
        const names = [...new Set(evs.map((e) => e.title || e.course_name || ""))].filter(Boolean);
        return (
          <button key={m} onClick={() => { const x = new Date(year, m, 1); setCursor(x); setView("month"); }}
            data-testid={`cal-year-month-${m}`}
            className={`border p-4 text-left transition ${evs.length ? "border-white/10 hover:border-[#ff0033]/50" : "border-white/5 opacity-40"}`}>
            <p className="font-heading text-xl uppercase tracking-tight text-white">{new Date(year, m, 1).toLocaleDateString(loc, { month: "long" })}</p>
            <p className="font-mono-lab text-xs text-[#ff0033] mt-1">{evs.length} {loc === "de-DE" ? "Termine" : "events"}</p>
            {names.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {names.slice(0, 3).map((n) => (
                  <li key={n} className="text-[11px] text-neutral-300 leading-tight truncate flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ff0033] shrink-0" />{n}
                  </li>
                ))}
                {names.length > 3 && <li className="text-[10px] text-neutral-500">+{names.length - 3} {loc === "de-DE" ? "weitere Kurse" : "more courses"}</li>}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Calendar;
